






















































import Component from 'vue-class-component';

// Shared types
import { Company, Map, RequestSpace } from '@coldtec/cos-types';

// Shared schemas
import { RequestSpaceSchema } from '@coldtec/cos-schemas';

// Store types
import {
    SpacesConfigData,
    SpacesAction,
    SpacesGetter,
    SpacesMutation,
    ToolSpace,
} from '@/store/store.types';

// Steps and step types
import Steps from '@/steps/steps';
import { Step } from '@/steps/types';

// The base class for every step
import BaseStep from '../BaseStep.vue';

// Modals
import AlertModal from '@/components/modal/Alert.vue';
import ConfirmModal from '@/components/modal/Confirm.vue';
import { InteractiveModalOptions } from '@/components/modal/types';

// Internal components
import SpaceNameModal, { CancelEvent } from './internals/SpaceNameModal.vue';
import SpaceAddButton from './internals/SpaceAddButton.vue';

// Spaces component
import Space from './internals/Space.vue';

// Used to type the confirm modal's actions
import { ButtonType } from '@/components/input/types';

// Validation
import Validator, { Errors } from '@/validation';

/**
 *  The first step: configuring the spaces.
 */
@Component({
    components: {
        Space,
        SpaceNameModal,
        SpaceAddButton,
    },
})
export default class SpacesStep extends BaseStep {

    public $refs!: {
        removeSpaceConfirm: ConfirmModal;
        spaceNameModal: SpaceNameModal;
        unknownAlert: AlertModal;
    };

    /**
     *  Holding the submitted state of the spaces, starting real-time validation after submition only.
     */
    private submitted = false;

    /**
     *  The description that is shown in the step title and during empty state.
     */
    private description = 'Stel voor elke ruimte waarvoor je airco wilt aanvragen een aparte ruimte in.';

    /**
     *  Create the validator for the spaces.
     */
    private validator = new Validator<RequestSpace>(RequestSpaceSchema);

    /**
     *  Used to get a map of all the added spaces.
     */
    private get spaces(): Map<ToolSpace> {

        return this.$store.getters[SpacesGetter.ADDED];

    }

    /**
     *  Used to get the fact if there are spaces added.
     */
    private get spacesAdded(): boolean {

        return this.$store.getters[SpacesGetter.COUNT_ADDED] > 0;

    }

    /**
     *  Used to get the data that is used to configure a space.
     */
    private get configData(): SpacesConfigData {

        return this.$store.state.spaces.configData;

    }

    /**
     *  Update the submitted state and footer accordingly when created.
     */
    private created(): void {

        /**
         *
         *  Directly start real-time validation if the spaces are already valid.
         *
         *  (i) For when the user navigated back to this step
         *
         */
        this.submitted = this.$store.state.spaces.valid;

        /**
         *  Set the adding of a space as the footer's floating action.
         */
        this.setFooterFloatingAction({
            icon: {
                width: 32,
                height: 32,
                path: '<path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16A16.02 16.02 0 0 0 16 0zm0 29.023C8.807 29.023 2.977 23.193 2.977 16S8.807 2.977 16 2.977 29.023 8.807 29.023 16A13.04 13.04 0 0 1 16 29.023h0zm6.326-12.651a1.34 1.34 0 0 1-1.34 1.34h-4.019v4.019a1.34 1.34 0 0 1-1.34 1.34 1.34 1.34 0 0 1-1.34-1.34v-4.019H10.27a1.34 1.34 0 0 1-1.34-1.34 1.34 1.34 0 0 1 1.34-1.34h4.019v-4.019a1.34 1.34 0 0 1 1.34-1.34 1.34 1.34 0 0 1 1.34 1.34v4.019h4.019a1.34 1.34 0 0 1 1.34 1.34z" />',
            },
            action: this.add,
        });

        /**
         *
         *  Ensure every space is valid before navigating to the systems.
         *
         *  (i) Enable the button if there are already spaces added when
         *  created, because it means the user navigated back to this step.
         *
         */
        this.setFooterNext({
            step: Steps[Step.Systems],
            disabled: !this.$store.getters[SpacesGetter.COUNT_ADDED],
            action: async() => {

                this.submitted = true;

                await this._validateAll();

                if (!this.$store.state.spaces.valid) {

                    return;

                }

                void this.$router.push(Step.Systems);

            },
        });

    }

    /**
     *  Invoke the base step's mounted hook when mounted.
     */
    private mounted(): void {

        this.stepMounted();

    }

    /**
     *  Used to dispatch the action for adding a space and
     *  directly opening the modal to set his name.
     */
    private async add(): Promise<void> {

        this.updateName(await this.$store.dispatch(SpacesAction.ADD) as ToolSpace, true);

        void this._checkValidity();

    }

    /**
     *
     *  Used to update the name of the forwarded
     *  space, using the space name modal.
     *
     *  @param space The (tool) space to update the name for
     *  @param isNew If the space for which the name should be updated is new
     *
     */
    private updateName(space: ToolSpace, isNew = false): void {

        this.$refs.spaceNameModal.open(space, isNew);

    }

    /**
     *
     *  Invoked when the space name modal is closed.
     *
     *  (i) Removes the tool space if it just got added,
     *  but entering his name was canceled.
     *
     *  @param event The cancel even of the space name modal
     *
     */
    private cancelUpdateName(event: CancelEvent): void {

        if (!event.isNew) {

            return;

        }

        this.$store.commit(SpacesAction.REMOVE, event.toolSpaceId);

    }

    /**
     *
     *  Invoked when the data of a space changed. Dispatching an action
     *  to reset the space's system selection and validating the space.
     *
     *  (i) Every change might effect the available suggestions, so
     *  the system must be reset at all times
     *
     */
    private update(space: ToolSpace): void {

        this.$store.dispatch(SpacesAction.RESET_SYSTEM_SELECTION, space.data.tool_space_id);

        this._validate(space);

    }

    /**
     *
     *  Used to remove the forwarded space, after the user confirmed the removal.
     *
     *  @param space The (tool) space to remove
     *  @param toolSpaceId The tool space id of the space to remove
     *
     */
    private async remove(space: ToolSpace, toolSpaceId: string): Promise<void> {

        /**
         *  Construct the options for the confirm modal.
         */
        const options: InteractiveModalOptions = {
            title: 'Ruimte verwijderen?',
            body: `De gegevens van "${ space.data.name }" zijn na het verwijderen niet terug te halen.`,
            cancel: {
                title: `${ space.data.name } niet verwijderen`,
                content: 'Annuleren',
            },
            confirm: {
                type: ButtonType.Warning,
                title: `${ space.data.name } verwijderen`,
                content: 'Ja, verwijderen',
            },
        };

        /**
         *  Only remove the space if the user confirmed the removal.
         */
        if (await this.$refs.removeSpaceConfirm.open(options)) {

            await this.$store.dispatch(SpacesAction.REMOVE, toolSpaceId);

            /**
             *  Programmatically open the last space.
             */
            const remaining: string[] = Object.keys(this.$store.getters[SpacesGetter.ADDED]);

            if (remaining.length) {

                this.toggle(remaining[remaining.length - 1]);

            }

        }

    }

    /**
     *
     *  Used to toggle the open state of a space.
     *
     *  @param toolSpaceId The tool space id of the space to toggle
     *
     */
    private toggle(toolSpaceId: string): void {

        this.$store.commit(SpacesMutation.TOGGLE, toolSpaceId);

    }

    /**
     *
     *  Used to validate the forwarded space (if submitted).
     *
     *  @param space The space to validate
     *
     */
    private async _validate(space: ToolSpace): Promise<void> {

        /**
         *  Always cast the data before validation to
         *  ensure the data has the types the API
         *  accepts (/the validation schema prescribes).
         */
        this._castSpace(space);

        /**
         *  Only validate the spaces when submitted.
         */
        if (!this.submitted) {

            return;

        }

        let valid = this.validator.validate(space.data, space.errors);

        /**
         *  Also ensure the user did not select unknown at
         *  both the insulation level and the location of
         *  the outside unit.
         */
        valid = this._validateUnknown(space, space.errors) && valid;

        /**
         *  Update the valid state of the space and invoke
         *  the method that checks the overall validity.
         */
        space.valid = valid;

        await this._checkValidity();

    }

    /**
     *  Used to validate all the added spaces.
     */
    private async _validateAll(): Promise<void> {

        for (const space of Object.values(this.$store.getters[SpacesGetter.ADDED]) as Array<ToolSpace>) {

            await this._validate(space);

        }

    }

    /**
     *
     *  Used to cast the data of the forwarded
     *  space to something the API accepts.
     *
     *  @param space The space to cast the data for
     *
     */
    private _castSpace(space: ToolSpace): void {

        space.data.with_outer_wall = space.data.with_outer_wall ? 1 : 0;
        space.data.used_for_heating = space.data.used_for_heating ? 1 : 0;

    }

    /**
     *
     *  Used to ensure the user did not select "unknown" for both the
     *  insulation level and outdoor unit location, because we can't
     *  compute the available systems if so.
     *
     *  (i) Which is true if both the selected models holds `null` as value
     *  (i) Also shows the unknown error alert modal if invalid
     *
     *  @param space The space to check the unknown states for
     *  @param errors The space's corresponding errors object
     *
     *  @returns If the unknown states are valid
     *
     */
    private _validateUnknown(space: ToolSpace, errors: Errors<RequestSpace>): boolean {

        let valid = true;

        if (space.data.insulation_level && space.data.outdoor_unit_location) {

            const different: { [type: string]: boolean; } = {
                insulationLevel: space.data.insulation_level === 'ccos-static-guid-insul-level-unknown',
                outdoorUnitLocation: space.data.outdoor_unit_location === 'ccos-static-guid-outsunitloc-unknown',
            };

            if (different.insulationLevel && different.outdoorUnitLocation) {

                this._showUnknownAlert(space);

                errors.outdoor_unit_location = errors.insulation_level = 'Omdat je onbekend hebt ingevuld voor isolatieniveau en locatie buitenunit kunnen we niet automatisch de systeem suggesties berekenen';

                valid = false;

            }

        }

        return valid;

    }

    /**
     *
     *  Used to show the unknown alert for the provided space.
     *
     *  (i) Showing when the user selected "unknown" for both
     *  the insulation level and location outside unit.
     *
     */
    private _showUnknownAlert(space: ToolSpace): void {

        const company: Company = this.$store.state.company.details;
        const message = `<p class="ccos-paragraph">Omdat je bij "<b>${ space.data.name }</b>" onbekend hebt ingevuld voor <i>isolatieniveau</i> en <i>locatie buitenunit</i> is het helaas onmogelijk om automatisch te berekenen welke systemen voor deze ruimte geschikt zijn.</p>`;
        const contact = `<p class="ccos-paragraph">Bel <a class="ccos-link ccos-link--external" href="tel:${ company.formatted_phone_number }">${ company.phone_number }</a> of mail naar <a class="ccos-link ccos-link--external" href="mailto:${ company.email }">${ company.email }</a> om een offerte aan te vragen.</p>`;

        void this.$refs.unknownAlert.open({
            title: 'Automatisch suggesties berekenen niet mogelijk',
            body: `${ message } ${ contact }`,
            width: '35rem',
        });

    }

    /**
     *  Used to dispatch the action to check the overall validity
     *  and update the footer's next button accordingly.
     */
    private async _checkValidity(): Promise<void> {

        await this.$store.dispatch(SpacesAction.CHECK_VALIDITY);

        this.setFooterNext({
            disabled: !this.$store.state.spaces.valid,
        });

    }

}
